import { Injectable, signal, computed, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import {
  SKUItem,
  DropdownOption,
  TreeNode,
  SKUType,
  SKUFormData,
  SKUDetail,
  SKUApiItem,
  SKUPaginatedResponse,
  SKUSearchPayload,
} from '../models/sku.interface';
import { SKU_STATUS_OPTIONS } from '../models/sku.constants';
import { SKUMapperService } from './sku-mapper.service';
import {
  SKURealApiService,
  SKUActionResponse,
  SkuSyncHistoryResponse,
  SkuActivationResult
} from './sku-real-api.service';

@Injectable({
  providedIn: 'root',
})
export class SKUService {
  private translateService = inject(TranslateService);

  constructor(
    private mapperService: SKUMapperService,
    private realApiService: SKURealApiService,
  ) {}

  // Static data for dropdowns and options
  readonly categoryOptions: DropdownOption[] = [
    { label: 'Test/1111', value: 'Test/1111' },
    { label: 'Snacks/General Category', value: 'Snacks/General Category' },
    { label: 'Snacks/112', value: 'Snacks/112' },
    { label: 'Daily Necessities/a', value: 'Daily Necessities/a' },
    { label: 'Daily Necessities/b', value: 'Daily Necessities/b' },
    {
      label: 'Electronic Products/Necklace',
      value: 'Electronic Products/Necklace',
    },
    {
      label: 'Electronic Products/Test 1012',
      value: 'Electronic Products/Test 1012',
    },
  ];

  // Computed status options that react to language changes
  get statusOptions(): DropdownOption[] {
    return SKU_STATUS_OPTIONS.map((option) => ({
      ...option,
      label: this.translateService.instant(option.label),
    }));
  }

  readonly categoryTreeOptions: TreeNode[] = [
    {
      label: 'Test',
      value: 'test',
      children: [{ label: '1111', value: 'Test/1111' }],
    },
    {
      label: 'Snacks',
      value: 'snacks',
      children: [
        { label: 'General Category', value: 'Snacks/General Category' },
        { label: '112', value: 'Snacks/112' },
      ],
    },
    {
      label: 'Daily Necessities',
      value: 'daily',
      children: [
        { label: 'a', value: 'Daily Necessities/a' },
        { label: 'b', value: 'Daily Necessities/b' },
      ],
    },
    {
      label: 'Electronic Products',
      value: 'electronics',
      children: [
        { label: 'Necklace', value: 'Electronic Products/Necklace' },
        { label: 'Test 1012', value: 'Electronic Products/Test 1012' },
      ],
    },
  ];

  // Legacy API Methods - these now delegate to the real API service
  getSkuItems(): Observable<SKUItem[]> {
    return this.realApiService
      .getSkuItems()
      .pipe(map((response) => response.data));
  }

  getSkuItemsByType(type: SKUType): Observable<SKUItem[]> {
    return this.realApiService
      .getSkuItemsByType(type)
      .pipe(map((response) => response.data));
  }

  searchSkuItems(
    searchData: SKUFormData,
    type: SKUType,
  ): Observable<SKUItem[]> {
    return this.realApiService
      .searchSkuItems(searchData, type)
      .pipe(map((response) => response.data));
  }

  getSkuItemById(id: number): Observable<SKUDetail> {
    return this.realApiService
      .getSkuItemById(id)
      .pipe(map((response) => response.data));
  }

  getSkuItemFromListById(id: number): Observable<SKUItem | null> {
    return this.getSkuItems().pipe(
      map((items) => items.find((item) => item.id === id) || null),
    );
  }

  getSkuDetailById(id: string): Observable<SKUDetail> {
    return this.realApiService
      .getSkuDetailById(id)
      .pipe(map((response) => response.data));
  }

  /** RBA-2268: paginated, chronological sync history for a single SKU. */
  getSkuSyncHistory(
    id: number,
    page: number = 0,
    size: number = 20,
  ): Observable<SkuSyncHistoryResponse> {
    return this.realApiService.getSkuSyncHistory(id, page, size);
  }

  deleteSkuItems(ids: number[]): Observable<boolean> {
    return this.realApiService
      .deleteSkuItems(ids)
      .pipe(map((response) => response.data));
  }

  submitSkuItems(ids: number[]): Observable<boolean> {
    return this.realApiService
      .submitSkuItems(ids)
      .pipe(map((response) => response.data));
  }

  auditSkuItem(
    id: number,
    approve: boolean,
    comment?: string,
  ): Observable<boolean> {
    return this.realApiService
      .auditSkuItem(id, approve, comment)
      .pipe(map((response) => response.data));
  }

  toggleSkuActivation(
    skuIds: number[],
    active: boolean,
    reason?: string,
  ): Observable<SkuActivationResult[]> {
    return this.realApiService.toggleSkuActivation(skuIds, active, reason);
  }

  addSkuItem(type: SKUType): Observable<boolean> {
    return this.realApiService
      .addSkuItem(type)
      .pipe(map((response) => response.data));
  }

  updateSkuItem(
    id: string,
    skuData: Partial<SKUDetail>,
  ): Observable<SKUActionResponse> {
    return this.realApiService.updateSkuItem(id, skuData);
  }

  // New API Methods for backend integration
  /**
   * Get SKU items with pagination using the new API format
   */
  getSkuItemsPaginated(
    page: number = 0,
    size: number = 20,
  ): Observable<SKUPaginatedResponse> {
    return this.realApiService.getSkuItemsPaginated(page, size);
  }

  /**
   * Search SKU items with pagination using the new API format
   */
  searchSkuItemsPaginated(
    searchPayload: SKUSearchPayload,
  ): Observable<SKUPaginatedResponse> {
    return this.realApiService.searchSkuItemsPaginated(searchPayload);
  }

  /**
   * Search SKU items with form data converted to search payload
   */
  searchSkuItemsWithFormData(
    formData: SKUFormData,
    page: number = 0,
    size: number = 20,
  ): Observable<SKUPaginatedResponse> {
    const searchPayload = this.mapperService.mapFormDataToSearchPayload(
      formData,
      page,
      size,
    );
    return this.searchSkuItemsPaginated(searchPayload);
  }

  /**
   * Convert paginated API response to legacy format for backward compatibility
   */
  getSkuItemsFromPaginatedResponse(
    paginatedResponse: SKUPaginatedResponse,
  ): SKUItem[] {
    return paginatedResponse.content.map((item, index) => ({
      ...item,
      serialNumber:
        paginatedResponse.number * paginatedResponse.size + index + 1,
    }));
  }

  /**
   * Get SKU detail from API item
   */
  getSkuDetailFromApiItem(apiItem: SKUApiItem): SKUDetail {
    return this.mapperService.mapApiItemToDetail(apiItem);
  }
}
